import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Group as GroupIcon,
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import folderPermissionService from '../../services/FolderPermissionService';
import groupControlService from '../../services/GroupControlService';
import userServiceInstance from '../../services/UserService';
import { isAdmin } from '../../utils/authUtils';
import AdminPageWrapper from './AdminPageWrapper';

const GroupManagement = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addUsersDialogOpen, setAddUsersDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [userData, setUserData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [usersLoading, setUsersLoading] = useState(true);

    useEffect(() => {
        loadGroups();
        loadUsers();
    }, []);

    const loadGroups = async () => {
        setLoading(true);
        setError(null);
        try {
            const groupsData = await groupControlService.getAllGroups();
            setGroups(groupsData);
        } catch (err) {
            console.error('Error loading groups:', err);
            setError('Failed to load groups. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        setUsersLoading(true);
        try {
            const response = await userServiceInstance.getAllUsers();
            setUsers(response || []);
        } catch (err) {
            console.error('Error loading users:', err);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) {
            setError('Group name is required');
            return;
        }

        // Validate group name format
        if (!/^[a-z0-9_]+$/.test(newGroupName)) {
            setError('Group name must contain only lowercase letters, numbers, and underscores');
            return;
        }

        try {
            const groupData = {
                name: newGroupName.trim(),
                description: newGroupDescription.trim() || undefined,
                memberEmails: [],
            };

            await groupControlService.createGroup(groupData);
            setSuccess('Group created successfully');
            setCreateDialogOpen(false);
            setNewGroupName('');
            setNewGroupDescription('');
            loadGroups();
        } catch (err) {
            console.error('Error creating group:', err);
            setError('Failed to create group. Please try again.');
        }
    };

    const handleEditGroup = async () => {
        if (!newGroupName.trim() || !selectedGroup) {
            setError('Group name is required');
            return;
        }

        // Validate group name format
        if (!/^[a-z0-9_]+$/.test(newGroupName)) {
            setError('Group name must contain only lowercase letters, numbers, and underscores');
            return;
        }

        try {
            const groupData = {
                name: newGroupName.trim(),
                description: newGroupDescription.trim() || undefined,
            };

            await groupControlService.updateGroup(selectedGroup._id, groupData);
            setSuccess('Group updated successfully');
            setEditDialogOpen(false);
            loadGroups();
        } catch (err) {
            console.error('Error updating group:', err);
            setError('Failed to update group. Please try again.');
        }
    };

    const handleDeleteGroup = async (groupId) => {
        try {
            await groupControlService.deleteGroup(groupId);
            setSuccess('Group deleted successfully');
            loadGroups();
        } catch (err) {
            console.error('Error deleting group:', err);
            setError('Failed to delete group. Please try again.');
        }
    };

    const handleAddUsers = async () => {
        if (!selectedGroup || selectedUsers.length === 0) {
            setError('Please select users to add');
            return;
        }

        try {
            await groupControlService.addUsersToGroup(selectedGroup.name, selectedUsers);
            setSuccess('Users added to group successfully');
            setAddUsersDialogOpen(false);
            setSelectedUsers([]);
            loadGroups();
        } catch (err) {
            console.error('Error adding users to group:', err);
            setError('Failed to add users to group. Please try again.');
        }
    };

    const handleRemoveUser = async (groupId, userEmail) => {
        try {
            await groupControlService.removeUsersFromGroup(groupId, [userEmail]);
            setSuccess('User removed from group successfully');
            loadGroups();
            if (selectedGroup && selectedGroup.name === groupId) {
                // Remove the user from the selectedGroup's memberEmails
                setSelectedGroup({
                    ...selectedGroup,
                    memberEmails: selectedGroup.memberEmails.filter((email) => email !== userEmail),
                });

                // Also remove from selectedUsers if it's there
                if (selectedUsers.includes(userEmail)) {
                    setSelectedUsers(selectedUsers.filter((email) => email !== userEmail));
                }
            }
        } catch (err) {
            console.error('Error removing user from group:', err);
            setError('Failed to remove user from group. Please try again.');
        }
    };

    const openEditDialog = (group) => {
        setSelectedGroup(group);
        setNewGroupName(group.name);
        setNewGroupDescription(group.description || '');
        setEditDialogOpen(true);
    };

    const openAddUsersDialog = async (group) => {
        setSelectedGroup(group);
        if (users.length === 0) {
            loadUsers();
        }
        // set selected users users based on users already in the group
        setSelectedUsers(group.memberEmails);
        setAddUsersDialogOpen(true);
    };

    const handleUserSelectionChange = (email) => {
        if (selectedUsers.includes(email)) {
            setSelectedUsers(selectedUsers.filter((e) => e !== email));
        } else {
            setSelectedUsers([...selectedUsers, email]);
        }
    };

    const handleMigratePermissions = async () => {
        try {
            // Show confirmation dialog
            const result = await Swal.fire({
                title: 'Migrate Folder Permissions',
                html: `
          <p>This will perform the following operations:</p>
          <ol>
            <li>Create user groups: <code>all_vaani_general_users</code> and <code>all_vaani_admin_users</code></li>
            <li>Add all users to the general users group</li>
            <li>Add all admin users to the admin users group</li>
            <li>Apply <strong>WRITE</strong> permission for general users to all folders</li>
            <li>Apply <strong>ADMIN</strong> permission for admin users to all folders</li>
          </ol>
          <p>This operation may take some time for large systems.</p>
        `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, migrate permissions',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) {
                return;
            }

            setLoading(true);

            const response = await folderPermissionService.migratePermissions();

            Swal.fire({
                title: 'Success',
                html: `
          <p>Permissions have been successfully migrated:</p>
          <ul>
            <li>${response.usersAdded} users added to general group</li>
            <li>${response.adminsAdded} users added to admin group</li>
            <li>${response.foldersProcessed} folders processed</li>
          </ul>
        `,
                icon: 'success',
            });

            // Refresh the group list
            loadGroups();
        } catch (error) {
            console.error('Error migrating permissions:', error);
            Swal.fire({
                title: 'Error',
                text: error.response?.data?.error || 'Failed to migrate permissions',
                icon: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminPageWrapper
            title="Group Management"
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Group Management
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Manage user groups for permission control.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    onClick={loadGroups}
                    disabled={loading}
                    sx={{ mr: 2 }}
                >
                    Refresh
                </Button>
                <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    color="primary"
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Create Group
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab label="Groups View" />
                    <Tab label="Users View" />
                </Tabs>
            </Box>

            {activeTab === 1 ? (
                <Paper sx={{ width: '100%', mb: 2 }}>
                    {usersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No users found
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Email</TableCell>
                                        <TableCell># Groups</TableCell>
                                        <TableCell>Groups</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => {
                                        const userGroups = groups.filter(
                                            (g) => g.memberEmails?.includes(user.email),
                                        );
                                        return (
                                            <TableRow key={user.email}>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <Chip label={userGroups.length} size="small" color={userGroups.length > 0 ? 'primary' : 'default'} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {userGroups.length > 0 ? (
                                                            userGroups.map((g) => (
                                                                <Chip
                                                                    key={g.name}
                                                                    label={g.name}
                                                                    size="small"
                                                                    color="primary"
                                                                />
                                                            ))
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                No groups
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            ) : (
            <Paper sx={{ width: '100%', mb: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : groups.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                            No groups found
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Group Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell># Members</TableCell>
                                    <TableCell>Members</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Updated By</TableCell>
                                    <TableCell>Updated At</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map((group) => (
                                    <TableRow key={group._id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                <Typography variant="body1">{group.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{group.description || 'No description'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={group.memberEmails?.length || 0}
                                                size="small"
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {group.memberEmails && group.memberEmails.length > 0 ? (
                                                    group.memberEmails.map((member) => (
                                                        <Chip
                                                            key={member}
                                                            label={member}
                                                            size="small"
                                                            onDelete={() => handleRemoveUser(group.name, member)}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No members
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{group.createdBy || 'N/A'}</TableCell>
                                        <TableCell>
                                            {group.createdAt ? new Date(group.createdAt).toLocaleString() : 'N/A'}
                                        </TableCell>
                                        <TableCell>{group.updatedBy || 'N/A'}</TableCell>
                                        <TableCell>
                                            {group.updatedAt ? new Date(group.updatedAt).toLocaleString() : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit Group">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => openEditDialog(group)}
                                                    size="small"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Add Members">
                                                <IconButton
                                                    color="success"
                                                    onClick={() => openAddUsersDialog(group)}
                                                    size="small"
                                                >
                                                    <PersonAddIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Group">
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteGroup(group.name)}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            )}

            {/* Create Group Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        fullWidth
                        variant="outlined"
                        value={newGroupName}
                        onChange={(e) => {
                            // Only allow lowercase letters, numbers, and underscores
                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                            setNewGroupName(value);
                        }}
                        helperText="Use only lowercase letters, numbers, and underscores (_)"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description (Optional)"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateGroup} variant="contained" color="primary" startIcon={<SaveIcon />}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Group Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        fullWidth
                        variant="outlined"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description (Optional)"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditGroup} variant="contained" color="primary" startIcon={<SaveIcon />}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Users Dialog */}
            <Dialog open={addUsersDialogOpen} onClose={() => setAddUsersDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Manage Group Members</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" gutterBottom>
                        Manage users for group: {selectedGroup?.name}
                    </Typography>
                    {users.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2">Loading users...</Typography>
                        </Box>
                    ) : (
                        <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                            {users.length === 0 ? (
                                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                                    No users available.
                                </Typography>
                            ) : (
                                users.map((user) => {
                                    const isMember = selectedGroup?.memberEmails?.includes(user.email);
                                    return (
                                        <ListItem
                                            key={user.email}
                                            sx={{ pr: 8 }} // Add extra padding for the buttons
                                        >
                                            <ListItemText
                                                primary={user.name || user.email}
                                                secondary={user.email}
                                                sx={
                                                    isMember
                                                        ? { '& .MuiListItemText-primary': { fontWeight: 'bold' } }
                                                        : {}
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                {isMember && (
                                                    <IconButton
                                                        edge="end"
                                                        color="error"
                                                        onClick={() => handleRemoveUser(selectedGroup.name, user.email)}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <Checkbox
                                                    edge="end"
                                                    checked={selectedUsers.includes(user.email)}
                                                    onChange={() => handleUserSelectionChange(user.email)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddUsersDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddUsers}
                        variant="contained"
                        color="primary"
                        disabled={selectedUsers.length === 0}
                        startIcon={<PersonAddIcon />}
                    >
                        Add Selected Users
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Migration Button - only show for admins */}
            {isAdmin() && (
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleMigratePermissions}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    >
                        {loading ? 'Migrating...' : 'Migrate Permissions'}
                    </Button>
                </Box>
            )}
        </AdminPageWrapper>
    );
};

export default GroupManagement;
